import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ControlledTableInput from '../../form-generator-v2/ControlledTableInput';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  buildPnlPayload,
  getPnlConfigForInventory,
  resetPnlOptionsInFormValues,
  toPnlTableRows
} from './inventoryPnlHelpers';

const PNL_OPTIONS_TABLE_FIELDS = [
  { title: 'P&L Option', key: 'key', type: 'datalist-text', required: false },
  { title: 'Value', key: 'value', type: 'number', required: false }
];

const getInventoryPnlContext = inventory => ({
  unitLocationId: inventory?.unitLocation?._id || inventory?.unitLocation,
  numberOfSections: inventory?.numberOfSections
});

const InventoryPnlFields = forwardRef(
  ({ inventory, appChoices, onInventoryUpdate, onValuesChange, skipApiSave = false, disabled = false }, ref) => {
    const [pnlValues, setPnlValues] = useState({
      pnlOptions: []
    });
    const latestValuesRef = useRef(pnlValues);

    const { unitLocationId, numberOfSections } = useMemo(() => getInventoryPnlContext(inventory), [inventory]);

    const dropdownOptions = useMemo(() => {
      const { pnlOptions } = getPnlConfigForInventory(appChoices, unitLocationId, numberOfSections);

      return {
        pnlOptions: pnlOptions.map(({ name }) => [name])
      };
    }, [appChoices, unitLocationId, numberOfSections]);

    const notifyValuesChange = values => {
      if (onValuesChange) {
        onValuesChange(buildPnlPayload(values));
      }
    };

    useEffect(() => {
      const nextValues = {
        pnlOptions: toPnlTableRows(inventory?.pnlOptions)
      };
      latestValuesRef.current = nextValues;
      setPnlValues(nextValues);
      notifyValuesChange(nextValues);
    }, [inventory?._id, inventory?.pnlOptions]);

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          if (!inventory?._id || disabled) return true;

          const payload = buildPnlPayload(latestValuesRef.current);

          if (skipApiSave) {
            if (onInventoryUpdate) {
              onInventoryUpdate({
                ...inventory,
                ...payload
              });
            }
            return true;
          }

          const { response, error } = await makeApiRequests({
            endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
            requestBody: payload,
            method: 'PUT'
          });

          if (error) {
            toast.error(error);
            return false;
          }

          if (onInventoryUpdate) {
            onInventoryUpdate({
              ...inventory,
              ...response
            });
          }

          return true;
        }
      }),
      [disabled, inventory, onInventoryUpdate, skipApiSave]
    );

    const updateField = (fieldKey, fieldValue) => {
      const nextValues = {
        ...pnlValues,
        [fieldKey]: fieldValue
      };
      latestValuesRef.current = nextValues;
      setPnlValues(nextValues);
      notifyValuesChange(nextValues);
    };

    const resetFieldToDefault = () => {
      const nextValues = resetPnlOptionsInFormValues(
        {
          unitLocation: unitLocationId,
          numberOfSections,
          ...pnlValues
        },
        appChoices
      );
      latestValuesRef.current = nextValues;
      setPnlValues(nextValues);
      notifyValuesChange(nextValues);
    };

    if (!inventory?._id) return null;

    return (
      <Row>
        <Col xs={12} md={6} className="mb-3 px-0">
          <ControlledTableInput
            id={`${inventory._id}-pnlOptions`}
            title="P&L Options"
            tableFields={PNL_OPTIONS_TABLE_FIELDS}
            value={pnlValues.pnlOptions}
            onChange={rows => updateField('pnlOptions', rows)}
            dropdownOptions={dropdownOptions.pnlOptions}
            disabled={disabled}
            defaultHideOptions
            showResetToDefault
            onResetToDefault={resetFieldToDefault}
          />
        </Col>
      </Row>
    );
  }
);

InventoryPnlFields.displayName = 'InventoryPnlFields';

export default InventoryPnlFields;
